import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "./ui/carousel";
import { Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = [
  { name: "Aïcha K.", country: "Sénégal", role: "Étudiante MBA", text: "L'ambiance est exigeante mais profondément humaine. J'ai trouvé un réseau qui me suivra toute ma carrière." },
  { name: "Kofi A.", country: "Ghana", role: "Étudiant BBA", text: "La pédagogie 70/30 m'a permis d'être opérationnel dès mon premier stage. Une expérience transformatrice." },
  { name: "Fatou D.", country: "Côte d'Ivoire", role: "Alumni MBA", text: "ECL m'a donné les outils pour créer ma propre entreprise. Le réseau Alumni est une vraie force." },
];

export const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState(FALLBACK);

  useEffect(() => {
    axios.get(`${API}/testimonials`)
      .then(({ data }) => { if (Array.isArray(data) && data.length > 0) setTestimonials(data); })
      .catch(() => {});
  }, []);

  return (
    <section className="section-padding bg-white" data-testid="testimonials-section">
      <div className="container-edge">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="overline mb-4">Témoignages</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight">
              Ils en <em className="text-navy-500">parlent</em>.
            </h2>
          </div>
        </div>

        <Carousel opts={{ loop: true, align: "start" }} className="w-full">
          <CarouselContent className="-ml-5">
            {testimonials.map((t, i) => (
              <CarouselItem key={t.id || i} className="pl-5 md:basis-1/2 lg:basis-1/3" data-testid={`testimonial-${i}`}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="bg-[#FAF8F3] border border-navy-100 p-7 h-full flex flex-col relative group hover:shadow-md transition-shadow"
                >
                  <Quote className="absolute top-5 right-5 h-6 w-6 text-red-500/20 group-hover:text-red-500/40 transition-colors" strokeWidth={1.5} />
                  <div className="absolute top-0 left-0 w-1 h-full bg-navy-100 group-hover:bg-red-500 transition-colors duration-500" />

                  <p className="text-[#444] leading-relaxed mb-6 text-[14.5px] flex-1 italic">
                    « {t.text} »
                  </p>

                  <div className="flex items-center gap-3 pt-5 border-t border-navy-100">
                    {t.avatar_url ? (
                      <img src={t.avatar_url} alt={t.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-red-500/30 shrink-0" loading="lazy" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-navy-500 to-navy-700 text-white flex items-center justify-center font-display text-lg ring-2 ring-red-500/30 shrink-0">
                        {(t.name || "?").trim().charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-display text-navy-700 text-base leading-tight">{t.name}</div>
                      <div className="text-navy-500 text-[10.5px] uppercase tracking-[0.15em] mt-1 truncate">
                        {t.role}{t.country ? ` · ${t.country}` : ""}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex gap-3 justify-end mt-8">
            <CarouselPrevious className="static translate-y-0 bg-transparent border border-navy-200 text-navy-700 hover:bg-navy-700 hover:text-white hover:border-navy-700 rounded-none h-11 w-11" data-testid="testimonial-prev" />
            <CarouselNext className="static translate-y-0 bg-transparent border border-navy-200 text-navy-700 hover:bg-navy-700 hover:text-white hover:border-navy-700 rounded-none h-11 w-11" data-testid="testimonial-next" />
          </div>
        </Carousel>
      </div>
    </section>
  );
};

export default TestimonialsSection;
